import type { UserMenuSubject } from '@/components/shared/UserMenu';
import { getCompanyInitials, getInitials, imgUrl } from '@/lib/utils/avatar';
import { hasCompany } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

interface UserMenuIdentity {
  /** Whom the trigger names: the company for a member, the person otherwise. */
  trigger: UserMenuSubject;
  company: UserMenuSubject;
  person: UserMenuSubject;
  email: string | undefined;
  hasCompany: boolean;
}

/**
 * Resolves the session into the shapes the user-menu cards render, so both the
 * client and management compositions read identity the same way.
 */
export function useUserMenuIdentity(): UserMenuIdentity {
  const user = useSessionStore(s => s.user);

  const person: UserMenuSubject = {
    name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
    imageUrl: imgUrl(user?.avatarFileUrl, 28),
    initials: user ? getInitials(user.firstName, user.lastName) : '',
  };

  const company: UserMenuSubject = {
    name: user?.company?.name ?? '',
    imageUrl: imgUrl(user?.company?.featuredImageFileUrl, 36),
    initials: user?.company?.name ? getCompanyInitials(user.company.name) : '',
  };

  const userHasCompany = hasCompany(user);

  return {
    // The trigger's avatar is 24px, not the cards' 28/36 — a separate request
    // so the CDN returns the size actually being displayed.
    trigger: userHasCompany
      ? {
          ...company,
          imageUrl: imgUrl(user?.company?.featuredImageFileUrl, 24),
        }
      : { ...person, imageUrl: imgUrl(user?.avatarFileUrl, 24) },
    company,
    person,
    email: user?.email,
    hasCompany: userHasCompany,
  };
}
